import { createContext, useState, useContext, ReactNode } from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  client: string;
  role: 'client' | 'admin';
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('portal-user');
    return saved ? JSON.parse(saved) : null;
  });
  const [isLoading, setIsLoading] = useState(false);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    
    // Simulation - remplacer par vraie API
    const users: Record<string, User> = {
      'gilles@fhvi.ch': {
        id: 'fhvi',
        name: 'Gilles Deschamps',
        email: 'gilles@fhvi.ch',
        client: 'fhvi',
        role: 'client'
      },
      'jean@pictet.com': {
        id: 'pictet',
        name: 'Jean-Pierre Moreau',
        email: 'jean@pictet.com',
        client: 'pictet',
        role: 'client'
      },
      'sophie@richemont.com': {
        id: 'richemont',
        name: 'Sophie Dubois',
        email: 'sophie@richemont.com',
        client: 'richemont',
        role: 'client'
      },
      'marc@sword.com': {
        id: 'sword',
        name: 'Marc Andersen',
        email: 'marc@sword.com',
        client: 'sword',
        role: 'client'
      },
      'thomas@caterpillar.com': {
        id: 'caterpillar',
        name: 'Thomas Mueller',
        email: 'thomas@caterpillar.com',
        client: 'caterpillar',
        role: 'client'
      },
      'klaus@bmw.com': {
        id: 'bmw',
        name: 'Klaus Schmidt',
        email: 'klaus@bmw.com',
        client: 'bmw',
        role: 'client'
      },
      'marie@mcdonalds.com': {
        id: 'mcdonalds',
        name: 'Marie Laurent',
        email: 'marie@mcdonalds.com',
        client: 'mcdonalds',
        role: 'client'
      },
      'philippe@pernod.com': {
        id: 'pernod',
        name: 'Philippe Martin',
        email: 'philippe@pernod.com',
        client: 'pernod',
        role: 'client'
      },
      'alexandra@lombard.com': {
        id: 'lombard',
        name: 'Alexandra Rousseau',
        email: 'alexandra@lombard.com',
        client: 'lombard',
        role: 'client'
      },
      'contact@formanova.ch': {
        id: 'formanova',
        name: 'Formanova',
        email: 'contact@formanova.ch',
        client: 'formanova',
        role: 'client'
      },
      'admin@infinity-cloud.ch': {
        id: 'admin',
        name: 'Admin',
        email: 'admin@infinity-cloud.ch',
        client: 'all',
        role: 'admin'
      }
    };

    await new Promise(resolve => setTimeout(resolve, 500));

    // Mots de passe par client
    const passwords: Record<string, string> = {
      'formanova': 'topsecret',
      'default': 'demo123'
    };

    const user = users[email];
    const expectedPassword = user ? (passwords[user.client] || passwords['default']) : '';
    if (user && password === expectedPassword) {
      setUser(user);
      localStorage.setItem('portal-user', JSON.stringify(user));
      setIsLoading(false);
      return true;
    }
    
    setIsLoading(false);
    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('portal-user');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};